/**
 * DueOrb — Sprint A1
 *
 * Compressed amber orb for due measures (only when count ≥ 5).
 * - Below 5: individual chips rendered by parent (QualityChipStrip)
 * - Click → dropdown with optional [Order] action button per row (live encounter only)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { QualityDropdownRow } from './QualityDropdownRow';

const DUE_COMPRESS_THRESHOLD = 5;

export function DueOrb({ count, chips = [], isLiveEncounter = false, encounterId, onAction }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Only render when threshold met
  if (count < DUE_COMPRESS_THRESHOLD) return null;

  // Sort: overdue first, then due; within each group by priority
  const sortedChips = [...chips].sort((a, b) => {
    const aOver = a.status === 'overdue' ? 0 : 1;
    const bOver = b.status === 'overdue' ? 0 : 1;
    return aOver - bOver;
  });

  return (
    <div ref={dropdownRef} className="relative" data-testid="due-orb">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold select-none transition-colors bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200 hover:shadow-sm"
        data-testid="due-orb-btn"
        aria-expanded={open}
        aria-label={`${count} due measures — click to expand`}>
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
        <Clock className="w-3 h-3 text-amber-600" />
        <span>{count}</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 w-80 bg-card rounded-xl border shadow-2xl z-50 overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-150"
          data-testid="due-orb-dropdown"
          style={{ maxHeight: '440px', overflowY: 'auto' }}>
          <div className="px-3 py-2 border-b bg-amber-50/50 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
              {count} Measure{count !== 1 ? 's' : ''} Due
            </span>
          </div>
          <div>
            {sortedChips.map(chip => (
              <QualityDropdownRow
                key={chip.measure_id}
                chip={chip}
                showActions={isLiveEncounter}
                onAction={onAction}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { DUE_COMPRESS_THRESHOLD };
